import { useEffect, useState } from 'react';
import { 
  Users, 
  BarChart3, 
  Settings, 
  Search,
  Ban,
  CheckCircle,
  Edit2,
  Save,
  X,
  CreditCard,
  Crown,
  Database,
  Activity
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

interface AdminStatsApiResponse {
  data: {
    total_users: number;
    active_users: number;
    banned_users: number;
    pro_users: number;
    agency_users: number;
    total_credits: number;
    generated_items_30d: number;
    credit_spend_30d: number;
    trends_30d: Array<{
      day: string;
      generated_items: number;
      credits_spent: number;
      transactions: number;
      debit_credits: number;
      grant_credits: number;
    }>;
  };
}

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: 'user' | 'admin' | 'superadmin';
  plan: 'free' | 'pro' | 'agency';
  creditsBalance: number;
  creditsSubscription: number;
  creditsPurchased: number;
  isBanned: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

interface AdminUsersApiResponse {
  data: Array<{
    id: string;
    email: string;
    full_name: string | null;
    role: 'user' | 'admin' | 'superadmin';
    plan_slug: 'free' | 'pro' | 'agency' | null;
    credits_subscription: number;
    credits_purchased: number;
    credits_bonus?: number;
    total_credits: number;
    is_banned: boolean;
    created_at: string;
    last_login_at?: string | null;
  }>;
}

interface AdminAIAction {
  action_key: string;
  display_name: string;
  description?: string | null;
  pricing_type: 'fixed' | 'token';
  fixed_credits: number;
  model_slug?: string | null;
  avg_input_tokens: number;
  avg_output_tokens: number;
  min_credits: number;
  max_credits?: number | null;
  is_active: boolean;
}

interface AdminModelRate {
  model_slug: string;
  provider: string;
  display_name: string;
  credits_per_1k_input_tokens: number;
  credits_per_1k_output_tokens: number;
  api_cost_per_1k_input_usd: number;
  api_cost_per_1k_output_usd: number;
  is_active: boolean;
}

interface AdminPlanAllocation {
  plan_slug: string;
  plan_name: string;
  price_monthly_cents: number;
  monthly_credits: number;
  trial_credits: number;
  provider_key: string;
  variant_id?: string | null;
  is_active: boolean;
}

interface AdminProviderSetting {
  provider_key: string;
  display_name: string;
  is_active: boolean;
  settings?: Record<string, unknown> | null;
}

interface AdminApifyActor {
  actor_key: string;
  actor_id: string;
  display_name: string;
  platform: string;
  pricing_model: 'free' | 'per_result' | 'per_run_plus_result' | 'compute_units';
  cost_per_1000_results_usd: number;
  run_start_cost_usd: number;
  default_max_items: number;
  hard_max_items: number;
  is_active: boolean;
  notes?: string | null;
}

interface AdminScrapingAction {
  action_key: string;
  display_name: string;
  description?: string | null;
  actor_key: string;
  actor_id?: string;
  platform?: string;
  default_max_items: number;
  pricing_type: 'fixed' | 'estimated_apify_cost' | 'actual_apify_cost';
  fixed_credits: number;
  credits_per_usd: number;
  min_credits: number;
  max_credits?: number | null;
  requires_admin_approval_over_usd?: number | null;
  is_active: boolean;
}

interface AdminApifyRun {
  id: string;
  email?: string | null;
  action_key: string;
  actor_key: string;
  actor_id: string;
  requested_items: number;
  actual_items?: number | null;
  estimated_cost_usd: number;
  actual_cost_usd?: number | null;
  estimated_credits: number;
  actual_credits?: number | null;
  status: string;
  error_message?: string | null;
  created_at: string;
}

interface AdminApifyUsage {
  spend_24h_usd: number;
  spend_7d_usd: number;
  spend_30d_usd: number;
  credits_30d: number;
  runs_30d: number;
  failed_30d: number;
  by_actor_30d?: Array<{ actor_key: string; runs: number; spend_usd: number; credits: number }>;
}

interface AdminListResponse<T> {
  data: T[];
}

const cardClass = 'brutalist-panel rounded-xl';
const tableWrapClass = 'overflow-x-auto rounded-xl border-2 border-border bg-card';
const tableHeadClass = 'bg-foreground text-background dark:bg-primary dark:text-primary-foreground';
const selectClass = 'rounded-md border-2 border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
const checkboxClass = 'h-4 w-4 rounded border-2 border-input accent-primary';

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-border bg-muted/30 p-6 text-sm">
      <p className="font-semibold text-foreground">{title}</p>
      <p className="mt-1 text-muted-foreground">{description}</p>
    </div>
  );
}

export function AdminPage() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<AdminUser>>({});
  const [adminStats, setAdminStats] = useState<AdminStatsApiResponse['data'] | null>(null);
  const [aiActions, setAiActions] = useState<AdminAIAction[]>([]);
  const [modelRates, setModelRates] = useState<AdminModelRate[]>([]);
  const [planAllocations, setPlanAllocations] = useState<AdminPlanAllocation[]>([]);
  const [providerSettings, setProviderSettings] = useState<AdminProviderSetting[]>([]);
  const [apifyActors, setApifyActors] = useState<AdminApifyActor[]>([]);
  const [scrapingActions, setScrapingActions] = useState<AdminScrapingAction[]>([]);
  const [apifyRuns, setApifyRuns] = useState<AdminApifyRun[]>([]);
  const [apifyUsage, setApifyUsage] = useState<AdminApifyUsage | null>(null);
  const [savingConfigKey, setSavingConfigKey] = useState<string | null>(null);
  const [newModelRate, setNewModelRate] = useState<AdminModelRate>({
    model_slug: '',
    provider: '',
    display_name: '',
    credits_per_1k_input_tokens: 0,
    credits_per_1k_output_tokens: 0,
    api_cost_per_1k_input_usd: 0,
    api_cost_per_1k_output_usd: 0,
    is_active: true,
  });

  useEffect(() => {
    const loadUsers = async () => {
      const response = await api.get<AdminUsersApiResponse>('/admin/users');
      const mapped = (response.data.data ?? []).map((item) => {
        const bonus = item.credits_bonus ?? 0;
        return {
          id: item.id,
          email: item.email,
          fullName: item.full_name || item.email,
          role: item.role,
          plan: item.plan_slug ?? 'free',
          creditsBalance: item.total_credits,
          creditsSubscription: item.credits_subscription,
          creditsPurchased: item.credits_purchased + bonus,
          isBanned: item.is_banned,
          createdAt: item.created_at,
          lastLoginAt: item.last_login_at ?? undefined,
        } as AdminUser;
      });
      setUsers(mapped);
    };

    const loadStats = async () => {
      const response = await api.get<AdminStatsApiResponse>('/admin/stats');
      setAdminStats(response.data.data);
    };

    const loadBillingConfig = async () => {
      const [actions, rates, allocations, providers, actors, scraping, runs, usage] = await Promise.all([
        api.get<AdminListResponse<AdminAIAction>>('/admin/ai-actions'),
        api.get<AdminListResponse<AdminModelRate>>('/admin/model-credit-rates'),
        api.get<AdminListResponse<AdminPlanAllocation>>('/admin/plans/credit-allocations'),
        api.get<AdminListResponse<AdminProviderSetting>>('/admin/payment-provider-settings'),
        api.get<AdminListResponse<AdminApifyActor>>('/admin/apify/actors'),
        api.get<AdminListResponse<AdminScrapingAction>>('/admin/scraping-actions'),
        api.get<AdminListResponse<AdminApifyRun>>('/admin/apify/runs'),
        api.get<{ data: AdminApifyUsage }>('/admin/apify/usage'),
      ]);
      setAiActions(actions.data.data ?? []);
      setModelRates(rates.data.data ?? []);
      setPlanAllocations(allocations.data.data ?? []);
      setProviderSettings(providers.data.data ?? []);
      setApifyActors(actors.data.data ?? []);
      setScrapingActions(scraping.data.data ?? []);
      setApifyRuns(runs.data.data ?? []);
      setApifyUsage(usage.data.data ?? null);
    };

    void loadUsers();
    void loadStats();
    void loadBillingConfig();
  }, []);

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'plans', label: 'Plans', icon: Crown },
    { id: 'actions', label: 'AI Actions', icon: CreditCard },
    { id: 'scraping', label: 'Apify / Scraping', icon: Database },
    { id: 'rates', label: 'Token Rates', icon: Settings },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEdit = (user: AdminUser) => {
    setEditingUser(user.id);
    setEditForm({
      role: user.role,
      plan: user.plan,
      creditsBalance: user.creditsBalance,
      creditsSubscription: user.creditsSubscription,
      creditsPurchased: user.creditsPurchased,
      isBanned: user.isBanned,
    });
  };

  const handleSave = async (userId: string) => {
    const payload = {
      role: editForm.role,
      plan: editForm.plan,
      credits_subscription: editForm.creditsSubscription,
      credits_purchased: editForm.creditsPurchased,
    };
    await api.put(`/admin/users/${userId}`, payload);

    setUsers(users.map(user =>
      user.id === userId
        ? {
            ...user,
            role: (editForm.role as AdminUser['role']) ?? user.role,
            plan: (editForm.plan as AdminUser['plan']) ?? user.plan,
            creditsSubscription: editForm.creditsSubscription ?? user.creditsSubscription,
            creditsPurchased: editForm.creditsPurchased ?? user.creditsPurchased,
            creditsBalance:
              (editForm.creditsSubscription ?? user.creditsSubscription) +
              (editForm.creditsPurchased ?? user.creditsPurchased),
          }
        : user
    ));
    setEditingUser(null);
    setEditForm({});
  };

  const handleCancel = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleBan = async (userId: string) => {
    const target = users.find((user) => user.id === userId);
    if (!target) {
      return;
    }
    const nextStatus = !target.isBanned;
    await api.put(`/admin/users/${userId}/ban`, { is_banned: nextStatus });
    setUsers(users.map(user =>
      user.id === userId ? { ...user, isBanned: nextStatus } : user
    ));
  };

  const updateAIActionField = <K extends keyof AdminAIAction>(index: number, field: K, value: AdminAIAction[K]) => {
    setAiActions((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));
  };

  const saveAIAction = async (action: AdminAIAction) => {
    setSavingConfigKey(`action:${action.action_key}`);
    try {
      await api.put(`/admin/ai-actions/${action.action_key}`, action);
    } finally {
      setSavingConfigKey(null);
    }
  };

  const updateModelRateField = <K extends keyof AdminModelRate>(index: number, field: K, value: AdminModelRate[K]) => {
    setModelRates((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));
  };

  const saveModelRate = async (rate: AdminModelRate) => {
    setSavingConfigKey(`rate:${rate.model_slug}`);
    try {
      await api.put(`/admin/model-credit-rates/${rate.model_slug}`, rate);
    } finally {
      setSavingConfigKey(null);
    }
  };

  const createModelRate = async () => {
    if (!newModelRate.model_slug || !newModelRate.provider || !newModelRate.display_name) {
      return;
    }
    setSavingConfigKey('rate:new');
    try {
      const response = await api.post<{ data: AdminModelRate }>('/admin/model-credit-rates', newModelRate);
      const saved = response.data.data;
      setModelRates((items) => {
        const withoutDuplicate = items.filter((item) => item.model_slug !== saved.model_slug);
        return [...withoutDuplicate, saved].sort((a, b) => `${a.provider}/${a.model_slug}`.localeCompare(`${b.provider}/${b.model_slug}`));
      });
      setNewModelRate({
        model_slug: '',
        provider: '',
        display_name: '',
        credits_per_1k_input_tokens: 0,
        credits_per_1k_output_tokens: 0,
        api_cost_per_1k_input_usd: 0,
        api_cost_per_1k_output_usd: 0,
        is_active: true,
      });
    } finally {
      setSavingConfigKey(null);
    }
  };

  const updatePlanAllocationField = <K extends keyof AdminPlanAllocation>(index: number, field: K, value: AdminPlanAllocation[K]) => {
    setPlanAllocations((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));
  };

  const updateApifyActorField = <K extends keyof AdminApifyActor>(index: number, field: K, value: AdminApifyActor[K]) => {
    setApifyActors((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));
  };

  const saveApifyActor = async (actor: AdminApifyActor) => {
    setSavingConfigKey(`apify-actor:${actor.actor_key}`);
    try {
      await api.put(`/admin/apify/actors/${actor.actor_key}`, actor);
    } finally {
      setSavingConfigKey(null);
    }
  };

  const updateScrapingActionField = <K extends keyof AdminScrapingAction>(index: number, field: K, value: AdminScrapingAction[K]) => {
    setScrapingActions((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));
  };

  const saveScrapingAction = async (action: AdminScrapingAction) => {
    setSavingConfigKey(`scraping-action:${action.action_key}`);
    try {
      await api.put(`/admin/scraping-actions/${action.action_key}`, action);
    } finally {
      setSavingConfigKey(null);
    }
  };

  const savePlanAllocation = async (allocation: AdminPlanAllocation) => {
    setSavingConfigKey(`plan:${allocation.plan_slug}`);
    try {
      await api.put(`/admin/plans/credit-allocations/${allocation.plan_slug}`, allocation);
    } finally {
      setSavingConfigKey(null);
    }
  };

  const stats = {
    totalUsers: adminStats?.total_users ?? users.length,
    activeUsers: adminStats?.active_users ?? users.filter(u => !u.isBanned).length,
    bannedUsers: adminStats?.banned_users ?? users.filter(u => u.isBanned).length,
    proUsers: adminStats?.pro_users ?? users.filter(u => u.plan === 'pro').length,
    agencyUsers: adminStats?.agency_users ?? users.filter(u => u.plan === 'agency').length,
    totalCredits: adminStats?.total_credits ?? users.reduce((sum, u) => sum + u.creditsBalance, 0),
    generatedItems30d: adminStats?.generated_items_30d ?? 0,
    creditSpend30d: adminStats?.credit_spend_30d ?? 0,
  };

  return (
    <div className="space-y-6">
      <div className="brutalist-panel rounded-2xl p-6">
        <p className="brutalist-label">Operations console</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight">Admin Panel</h1>
        <p className="text-muted-foreground">Manage users, analytics, pricing, scraping, and provider settings.</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={cardClass}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsers}</div>
          </CardContent>
        </Card>
        <Card className={cardClass}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeUsers}</div>
          </CardContent>
        </Card>
        <Card className={cardClass}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pro Users</CardTitle>
            <Crown className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.proUsers}</div>
          </CardContent>
        </Card>
        <Card className={cardClass}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
            <CreditCard className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCredits.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b-2 border-border pb-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 border-2 px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] transition-colors',
                activeTab === tab.id
                  ? 'border-primary bg-primary text-primary-foreground shadow-[3px_3px_0_hsl(var(--foreground)/0.18)]'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card className={cardClass}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>User Management</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  aria-label="Search users" placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <CardDescription>
              Manage user accounts, roles, credits, and permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={tableWrapClass}>
              {filteredUsers.length === 0 ? (
                <div className="p-4">
                  <EmptyState title="No users found" description="Try a different email or name filter. User controls will appear here when accounts match." />
                </div>
              ) : (
              <table className="w-full text-sm">
                <thead className={tableHeadClass}>
                  <tr>
                    <th className="text-left p-3 font-medium">User</th>
                    <th className="text-left p-3 font-medium">Role</th>
                    <th className="text-left p-3 font-medium">Plan</th>
                    <th className="text-left p-3 font-medium">Credits</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={user.isBanned ? 'bg-red-500/5' : ''}>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{user.fullName}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        {editingUser === user.id ? (
                          <select
                            className={selectClass}
                            aria-label={`Role for ${user.email}`}
                            value={editForm.role}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value as AdminUser['role'] })}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                            <option value="superadmin">Superadmin</option>
                          </select>
                        ) : (
                          <span className={cn(
                            'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                            user.role === 'superadmin' && 'bg-purple-500/10 text-purple-500',
                            user.role === 'admin' && 'bg-blue-500/10 text-blue-500',
                            user.role === 'user' && 'bg-gray-500/10 text-gray-500'
                          )}>
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {editingUser === user.id ? (
                          <select
                            className={selectClass}
                            aria-label={`Plan for ${user.email}`}
                            value={editForm.plan}
                            onChange={(e) => setEditForm({ ...editForm, plan: e.target.value as AdminUser['plan'] })}
                          >
                            <option value="free">Free</option>
                            <option value="pro">Pro</option>
                            <option value="agency">Agency</option>
                          </select>
                        ) : (
                          <span className="capitalize">{user.plan}</span>
                        )}
                      </td>
                      <td className="p-3">
                        {editingUser === user.id ? (
                          <div className="space-y-1">
                            <Input
                              aria-label={`Subscription credits for ${user.email}`}
                              type="number"
                              className="w-24 h-7 text-sm"
                              value={editForm.creditsSubscription}
                              onChange={(e) => setEditForm({ ...editForm, creditsSubscription: parseInt(e.target.value, 10) || 0 })}
                            />
                            <Input
                              aria-label={`Purchased credits for ${user.email}`}
                              type="number"
                              className="w-24 h-7 text-sm"
                              value={editForm.creditsPurchased}
                              onChange={(e) => setEditForm({ ...editForm, creditsPurchased: parseInt(e.target.value, 10) || 0 })}
                            />
                          </div>
                        ) : (
                          <div className="text-sm">
                            <p>{user.creditsBalance.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">
                              Sub: {user.creditsSubscription.toLocaleString()}
                            </p>
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={cn(
                          'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                          user.isBanned 
                            ? 'bg-red-500/10 text-red-500' 
                            : 'bg-green-500/10 text-green-500'
                        )}>
                          {user.isBanned ? 'Banned' : 'Active'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {editingUser === user.id ? (
                            <>
                              <Button size="sm" variant="ghost" aria-label={`Save changes for ${user.email}`} onClick={() => void handleSave(user.id)}>
                                <Save className="h-4 w-4 text-green-500" />
                              </Button>
                              <Button size="sm" variant="ghost" aria-label={`Cancel editing ${user.email}`} onClick={handleCancel}>
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="ghost" aria-label={`Edit ${user.email}`} onClick={() => handleEdit(user)}>
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm" 
                                variant="ghost"
                                aria-label={user.isBanned ? `Unban ${user.email}` : `Ban ${user.email}`}
                                onClick={() => void handleBan(user.id)}
                              >
                                {user.isBanned ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Ban className="h-4 w-4 text-red-500" />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>Credit Usage</CardTitle>
              <CardDescription>Live distribution across current user base</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Total credits assigned</span>
                <span className="font-semibold">{stats.totalCredits.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Average credits per user</span>
                <span className="font-semibold">
                  {stats.totalUsers > 0
                    ? Math.round(stats.totalCredits / stats.totalUsers).toLocaleString()
                    : '0'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Pro/Agency users</span>
                <span className="font-semibold">{stats.proUsers + stats.agencyUsers}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Credit spend (30d)</span>
                <span className="font-semibold">{stats.creditSpend30d.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>User Growth</CardTitle>
              <CardDescription>Current account mix and status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Active users</span>
                <span className="font-semibold">{stats.activeUsers}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Banned users</span>
                <span className="font-semibold">{stats.bannedUsers}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Pro users</span>
                <span className="font-semibold">{stats.proUsers}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Agency users</span>
                <span className="font-semibold">{stats.agencyUsers}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Generated items (30d)</span>
                <span className="font-semibold">{stats.generatedItems30d}</span>
              </div>
              {adminStats?.trends_30d?.length ? (
                <div className="mt-2 rounded-md border p-2 text-xs text-muted-foreground space-y-1">
                  {adminStats.trends_30d.slice(-5).map((entry) => (
                    <div key={entry.day} className="flex items-center justify-between">
                      <span>{entry.day}</span>
                      <span>
                        {entry.generated_items} items | {entry.credits_spent} credits
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle>Plan Credit Allocations</CardTitle>
            <CardDescription>Credits and Lemon Squeezy variants are editable rows, not hardcoded in code.</CardDescription>
          </CardHeader>
          <CardContent>
            {planAllocations.length === 0 ? (
              <EmptyState title="No plan allocations loaded" description="Billing plan controls will appear here once the admin catalog responds." />
            ) : null}
            <div className={tableWrapClass}>
              <table className="w-full text-sm">
                <thead className={tableHeadClass}>
                  <tr>
                    <th className="text-left p-3 font-medium">Plan</th>
                    <th className="text-left p-3 font-medium">Monthly credits</th>
                    <th className="text-left p-3 font-medium">Trial credits</th>
                    <th className="text-left p-3 font-medium">Variant ID</th>
                    <th className="text-left p-3 font-medium">Active</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {planAllocations.map((allocation, index) => (
                    <tr key={allocation.plan_slug}>
                      <td className="p-3 font-medium">{allocation.plan_name}</td>
                      <td className="p-3">
                        <Input aria-label={`Monthly credits for ${allocation.plan_name}`} type="number" className="w-32" value={allocation.monthly_credits} onChange={(e) => updatePlanAllocationField(index, 'monthly_credits', parseInt(e.target.value, 10) || 0)} />
                      </td>
                      <td className="p-3">
                        <Input aria-label={`Trial credits for ${allocation.plan_name}`} type="number" className="w-32" value={allocation.trial_credits} onChange={(e) => updatePlanAllocationField(index, 'trial_credits', parseInt(e.target.value, 10) || 0)} />
                      </td>
                      <td className="p-3">
                        <Input aria-label={`Lemon Squeezy variant ID for ${allocation.plan_name}`} className="w-64" value={allocation.variant_id ?? ''} onChange={(e) => updatePlanAllocationField(index, 'variant_id', e.target.value)} placeholder="ls_variant_id" />
                      </td>
                      <td className="p-3">
                        <input type="checkbox" className={checkboxClass} aria-label={`Toggle ${allocation.plan_name} plan active state`} checked={allocation.is_active} onChange={(e) => updatePlanAllocationField(index, 'is_active', e.target.checked)} />
                      </td>
                      <td className="p-3">
                        <Button size="sm" onClick={() => void savePlanAllocation(allocation)} disabled={savingConfigKey === `plan:${allocation.plan_slug}`}>Save</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Actions Tab */}
      {activeTab === 'actions' && (
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle>AI Action Pricing</CardTitle>
            <CardDescription>Toggle actions and change fixed/token-based credit costs without deploys.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={tableWrapClass}>
              <table className="w-full text-sm">
                <thead className={tableHeadClass}>
                  <tr>
                    <th className="text-left p-3 font-medium">Action</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-left p-3 font-medium">Fixed</th>
                    <th className="text-left p-3 font-medium">Model</th>
                    <th className="text-left p-3 font-medium">Avg tokens</th>
                    <th className="text-left p-3 font-medium">Min/Max</th>
                    <th className="text-left p-3 font-medium">Active</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {aiActions.map((action, index) => (
                    <tr key={action.action_key}>
                      <td className="p-3">
                        <p className="font-medium">{action.display_name}</p>
                        <p className="text-xs text-muted-foreground">{action.action_key}</p>
                      </td>
                      <td className="p-3">
                        <select aria-label={`Pricing type for ${action.display_name}`} className={selectClass} value={action.pricing_type} onChange={(e) => updateAIActionField(index, 'pricing_type', e.target.value as AdminAIAction['pricing_type'])}>
                          <option value="fixed">fixed</option>
                          <option value="token">token</option>
                        </select>
                      </td>
                      <td className="p-3"><Input aria-label={`Fixed credits for ${action.display_name}`} type="number" className="w-24" value={action.fixed_credits} onChange={(e) => updateAIActionField(index, 'fixed_credits', parseInt(e.target.value, 10) || 0)} /></td>
                      <td className="p-3"><Input aria-label={`Model slug for ${action.display_name}`} className="w-40" value={action.model_slug ?? ''} onChange={(e) => updateAIActionField(index, 'model_slug', e.target.value || null)} /></td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Input aria-label={`Average input tokens for ${action.display_name}`} type="number" className="w-24" value={action.avg_input_tokens} onChange={(e) => updateAIActionField(index, 'avg_input_tokens', parseInt(e.target.value, 10) || 0)} />
                          <Input aria-label={`Average output tokens for ${action.display_name}`} type="number" className="w-24" value={action.avg_output_tokens} onChange={(e) => updateAIActionField(index, 'avg_output_tokens', parseInt(e.target.value, 10) || 0)} />
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Input aria-label={`Minimum credits for ${action.display_name}`} type="number" className="w-20" value={action.min_credits} onChange={(e) => updateAIActionField(index, 'min_credits', parseInt(e.target.value, 10) || 0)} />
                          <Input aria-label={`Maximum credits for ${action.display_name}`} type="number" className="w-20" value={action.max_credits ?? ''} onChange={(e) => updateAIActionField(index, 'max_credits', e.target.value ? parseInt(e.target.value, 10) : null)} />
                        </div>
                      </td>
                      <td className="p-3"><input type="checkbox" className={checkboxClass} aria-label={`Toggle ${action.display_name} active state`} checked={action.is_active} onChange={(e) => updateAIActionField(index, 'is_active', e.target.checked)} /></td>
                      <td className="p-3"><Button size="sm" onClick={() => void saveAIAction(action)} disabled={savingConfigKey === `action:${action.action_key}`}>Save</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Apify / Scraping Tab */}
      {activeTab === 'scraping' && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className={cardClass}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Apify spend 24h</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">${(apifyUsage?.spend_24h_usd ?? 0).toFixed(2)}</div></CardContent>
            </Card>
            <Card className={cardClass}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Apify spend 7d</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">${(apifyUsage?.spend_7d_usd ?? 0).toFixed(2)}</div></CardContent>
            </Card>
            <Card className={cardClass}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Apify spend 30d</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">${(apifyUsage?.spend_30d_usd ?? 0).toFixed(2)}</div></CardContent>
            </Card>
            <Card className={cardClass}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Scraping credits 30d</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{(apifyUsage?.credits_30d ?? 0).toLocaleString()}</div></CardContent>
            </Card>
          </div>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" /> Apify Actors</CardTitle>
              <CardDescription>Legacy Supabase actor IDs are seeded here. Never run unbounded actors: set default/hard item limits and cost per 1K.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={tableWrapClass}>
                <table className="w-full text-sm">
                  <thead className={tableHeadClass}>
                    <tr>
                      <th className="text-left p-3 font-medium">Actor</th>
                      <th className="text-left p-3 font-medium">Pricing</th>
                      <th className="text-left p-3 font-medium">Cost</th>
                      <th className="text-left p-3 font-medium">Limits</th>
                      <th className="text-left p-3 font-medium">Active</th>
                      <th className="text-left p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {apifyActors.map((actor, index) => (
                      <tr key={actor.actor_key}>
                        <td className="p-3 min-w-[280px]">
                          <Input aria-label={`Display name for ${actor.actor_key} actor`} className="mb-2" value={actor.display_name} onChange={(e) => updateApifyActorField(index, 'display_name', e.target.value)} />
                          <p className="text-xs font-medium">{actor.actor_key}</p>
                          <Input aria-label={`Actor ID for ${actor.actor_key}`} className="mt-1" value={actor.actor_id} onChange={(e) => updateApifyActorField(index, 'actor_id', e.target.value)} />
                        </td>
                        <td className="p-3">
                          <select aria-label={`Pricing model for ${actor.display_name}`} className={selectClass} value={actor.pricing_model} onChange={(e) => updateApifyActorField(index, 'pricing_model', e.target.value as AdminApifyActor['pricing_model'])}>
                            <option value="free">free</option>
                            <option value="per_result">per_result</option>
                            <option value="per_run_plus_result">per_run_plus_result</option>
                            <option value="compute_units">compute_units</option>
                          </select>
                          <p className="mt-2 text-xs text-muted-foreground">{actor.platform}</p>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Input aria-label={`Cost per 1000 results for ${actor.display_name}`} type="number" step="0.000001" className="w-28" value={actor.cost_per_1000_results_usd} onChange={(e) => updateApifyActorField(index, 'cost_per_1000_results_usd', parseFloat(e.target.value) || 0)} />
                            <Input aria-label={`Run start cost for ${actor.display_name}`} type="number" step="0.000001" className="w-28" value={actor.run_start_cost_usd} onChange={(e) => updateApifyActorField(index, 'run_start_cost_usd', parseFloat(e.target.value) || 0)} />
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">$/1K results | run start $</p>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Input aria-label={`Default item limit for ${actor.display_name}`} type="number" className="w-24" value={actor.default_max_items} onChange={(e) => updateApifyActorField(index, 'default_max_items', parseInt(e.target.value, 10) || 1)} />
                            <Input aria-label={`Hard item limit for ${actor.display_name}`} type="number" className="w-24" value={actor.hard_max_items} onChange={(e) => updateApifyActorField(index, 'hard_max_items', parseInt(e.target.value, 10) || 1)} />
                          </div>
                        </td>
                        <td className="p-3"><input type="checkbox" className={checkboxClass} aria-label={`Toggle ${actor.display_name} actor active state`} checked={actor.is_active} onChange={(e) => updateApifyActorField(index, 'is_active', e.target.checked)} /></td>
                        <td className="p-3"><Button size="sm" onClick={() => void saveApifyActor(actor)} disabled={savingConfigKey === `apify-actor:${actor.actor_key}`}>Save</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>Scraping Actions</CardTitle>
              <CardDescription>Map product actions to actors and convert Apify USD cost into BeLoud credits.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={tableWrapClass}>
                <table className="w-full text-sm">
                  <thead className={tableHeadClass}>
                    <tr>
                      <th className="text-left p-3 font-medium">Action</th>
                      <th className="text-left p-3 font-medium">Actor</th>
                      <th className="text-left p-3 font-medium">Pricing</th>
                      <th className="text-left p-3 font-medium">Credits</th>
                      <th className="text-left p-3 font-medium">Approval</th>
                      <th className="text-left p-3 font-medium">Active</th>
                      <th className="text-left p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {scrapingActions.map((action, index) => (
                      <tr key={action.action_key}>
                        <td className="p-3 min-w-[220px]">
                          <p className="font-medium">{action.display_name}</p>
                          <p className="text-xs text-muted-foreground">{action.action_key}</p>
                        </td>
                        <td className="p-3"><Input aria-label={`Actor key for ${action.display_name}`} className="w-64" value={action.actor_key} onChange={(e) => updateScrapingActionField(index, 'actor_key', e.target.value)} /></td>
                        <td className="p-3">
                          <select aria-label={`Pricing type for ${action.display_name}`} className={selectClass} value={action.pricing_type} onChange={(e) => updateScrapingActionField(index, 'pricing_type', e.target.value as AdminScrapingAction['pricing_type'])}>
                            <option value="fixed">fixed</option>
                            <option value="estimated_apify_cost">estimated_apify_cost</option>
                            <option value="actual_apify_cost">actual_apify_cost</option>
                          </select>
                          <Input aria-label={`Default item limit for ${action.display_name}`} type="number" className="mt-2 w-24" value={action.default_max_items} onChange={(e) => updateScrapingActionField(index, 'default_max_items', parseInt(e.target.value, 10) || 1)} />
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Input aria-label={`Credits per USD for ${action.display_name}`} type="number" className="w-24" value={action.credits_per_usd} onChange={(e) => updateScrapingActionField(index, 'credits_per_usd', parseInt(e.target.value, 10) || 0)} />
                            <Input aria-label={`Minimum credits for ${action.display_name}`} type="number" className="w-24" value={action.min_credits} onChange={(e) => updateScrapingActionField(index, 'min_credits', parseInt(e.target.value, 10) || 0)} />
                            <Input aria-label={`Maximum credits for ${action.display_name}`} type="number" className="w-24" value={action.max_credits ?? ''} onChange={(e) => updateScrapingActionField(index, 'max_credits', e.target.value ? parseInt(e.target.value, 10) : null)} />
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">credits/$ | min | max</p>
                        </td>
                        <td className="p-3"><Input aria-label={`Admin approval USD threshold for ${action.display_name}`} type="number" step="0.01" className="w-24" value={action.requires_admin_approval_over_usd ?? ''} onChange={(e) => updateScrapingActionField(index, 'requires_admin_approval_over_usd', e.target.value ? parseFloat(e.target.value) : null)} /></td>
                        <td className="p-3"><input type="checkbox" className={checkboxClass} aria-label={`Toggle ${action.display_name} scraping action active state`} checked={action.is_active} onChange={(e) => updateScrapingActionField(index, 'is_active', e.target.checked)} /></td>
                        <td className="p-3"><Button size="sm" onClick={() => void saveScrapingAction(action)} disabled={savingConfigKey === `scraping-action:${action.action_key}`}>Save</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" /> Apify Run Logs</CardTitle>
              <CardDescription>Last 100 runs with estimated vs actual cost and credits.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={tableWrapClass}>
                <table className="w-full text-sm">
                  <thead className={tableHeadClass}>
                    <tr>
                      <th className="text-left p-3 font-medium">Run</th>
                      <th className="text-left p-3 font-medium">Actor</th>
                      <th className="text-left p-3 font-medium">Items</th>
                      <th className="text-left p-3 font-medium">Cost</th>
                      <th className="text-left p-3 font-medium">Credits</th>
                      <th className="text-left p-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {apifyRuns.map((run) => (
                      <tr key={run.id}>
                        <td className="p-3"><p className="font-medium">{run.action_key}</p><p className="text-xs text-muted-foreground">{run.email ?? 'system'} · {new Date(run.created_at).toLocaleString()}</p></td>
                        <td className="p-3"><p>{run.actor_key}</p><p className="text-xs text-muted-foreground">{run.actor_id}</p></td>
                        <td className="p-3">{run.actual_items ?? run.requested_items}/{run.requested_items}</td>
                        <td className="p-3">${Number(run.actual_cost_usd ?? run.estimated_cost_usd ?? 0).toFixed(4)}</td>
                        <td className="p-3">{Number(run.actual_credits ?? run.estimated_credits ?? 0).toLocaleString()}</td>
                        <td className="p-3"><span className="text-xs px-2 py-1 rounded-full bg-muted">{run.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Token Rates Tab */}
      {activeTab === 'rates' && (
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle>Token ↔ Credit Rates</CardTitle>
            <CardDescription>Per-model input/output token rates used by token-priced actions.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 rounded-lg border p-4 space-y-3">
              <div>
                <p className="font-medium">Add / upsert model connector</p>
                <p className="text-xs text-muted-foreground">Use this for Kimi, DeepSeek, Gemini, GPT, Claude or any provider-specific model slug.</p>
              </div>
              <div className="grid md:grid-cols-4 gap-3">
                <Input aria-label="New model slug" placeholder="model slug" value={newModelRate.model_slug} onChange={(e) => setNewModelRate({ ...newModelRate, model_slug: e.target.value })} />
                <Input aria-label="New model provider" placeholder="provider" value={newModelRate.provider} onChange={(e) => setNewModelRate({ ...newModelRate, provider: e.target.value })} />
                <Input aria-label="New model display name" placeholder="display name" value={newModelRate.display_name} onChange={(e) => setNewModelRate({ ...newModelRate, display_name: e.target.value })} />
                <Button onClick={() => void createModelRate()} disabled={savingConfigKey === 'rate:new' || !newModelRate.model_slug || !newModelRate.provider || !newModelRate.display_name}>Add model</Button>
              </div>
              <div className="grid md:grid-cols-5 gap-3">
                <Input aria-label="New model input credits per 1000 tokens" type="number" placeholder="input credits/1K" value={newModelRate.credits_per_1k_input_tokens} onChange={(e) => setNewModelRate({ ...newModelRate, credits_per_1k_input_tokens: parseInt(e.target.value, 10) || 0 })} />
                <Input aria-label="New model output credits per 1000 tokens" type="number" placeholder="output credits/1K" value={newModelRate.credits_per_1k_output_tokens} onChange={(e) => setNewModelRate({ ...newModelRate, credits_per_1k_output_tokens: parseInt(e.target.value, 10) || 0 })} />
                <Input aria-label="New model API input cost per 1000 tokens" type="number" step="0.000001" placeholder="API input $/1K" value={newModelRate.api_cost_per_1k_input_usd} onChange={(e) => setNewModelRate({ ...newModelRate, api_cost_per_1k_input_usd: parseFloat(e.target.value) || 0 })} />
                <Input aria-label="New model API output cost per 1000 tokens" type="number" step="0.000001" placeholder="API output $/1K" value={newModelRate.api_cost_per_1k_output_usd} onChange={(e) => setNewModelRate({ ...newModelRate, api_cost_per_1k_output_usd: parseFloat(e.target.value) || 0 })} />
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" className={checkboxClass} checked={newModelRate.is_active} onChange={(e) => setNewModelRate({ ...newModelRate, is_active: e.target.checked })} /> Active</label>
              </div>
            </div>
            <div className={tableWrapClass}>
              <table className="w-full text-sm">
                <thead className={tableHeadClass}>
                  <tr>
                    <th className="text-left p-3 font-medium">Model</th>
                    <th className="text-left p-3 font-medium">Input credits / 1K</th>
                    <th className="text-left p-3 font-medium">Output credits / 1K</th>
                    <th className="text-left p-3 font-medium">API cost input/output</th>
                    <th className="text-left p-3 font-medium">Active</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {modelRates.map((rate, index) => (
                    <tr key={rate.model_slug}>
                      <td className="p-3">
                        <p className="font-medium">{rate.display_name}</p>
                        <p className="text-xs text-muted-foreground">{rate.provider} / {rate.model_slug}</p>
                      </td>
                      <td className="p-3"><Input aria-label={`Input credits per 1000 tokens for ${rate.display_name}`} type="number" className="w-28" value={rate.credits_per_1k_input_tokens} onChange={(e) => updateModelRateField(index, 'credits_per_1k_input_tokens', parseInt(e.target.value, 10) || 0)} /></td>
                      <td className="p-3"><Input aria-label={`Output credits per 1000 tokens for ${rate.display_name}`} type="number" className="w-28" value={rate.credits_per_1k_output_tokens} onChange={(e) => updateModelRateField(index, 'credits_per_1k_output_tokens', parseInt(e.target.value, 10) || 0)} /></td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Input aria-label={`API input cost for ${rate.display_name}`} type="number" step="0.000001" className="w-28" value={rate.api_cost_per_1k_input_usd} onChange={(e) => updateModelRateField(index, 'api_cost_per_1k_input_usd', parseFloat(e.target.value) || 0)} />
                          <Input aria-label={`API output cost for ${rate.display_name}`} type="number" step="0.000001" className="w-28" value={rate.api_cost_per_1k_output_usd} onChange={(e) => updateModelRateField(index, 'api_cost_per_1k_output_usd', parseFloat(e.target.value) || 0)} />
                        </div>
                      </td>
                      <td className="p-3"><input type="checkbox" className={checkboxClass} aria-label={`Toggle ${rate.display_name} model active state`} checked={rate.is_active} onChange={(e) => updateModelRateField(index, 'is_active', e.target.checked)} /></td>
                      <td className="p-3"><Button size="sm" onClick={() => void saveModelRate(rate)} disabled={savingConfigKey === `rate:${rate.model_slug}`}>Save</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle>Payment Provider Settings</CardTitle>
            <CardDescription>Read current billing provider configuration. Secrets stay in backend env; UI edits only safe config rows.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {providerSettings.map((provider) => (
              <div key={provider.provider_key} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">{provider.display_name}</p>
                  <p className="text-sm text-muted-foreground">{provider.provider_key}</p>
                  <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{JSON.stringify(provider.settings ?? {}, null, 2)}</pre>
                </div>
                <span className={cn('text-xs px-2 py-1 rounded-full', provider.is_active ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500')}>
                  {provider.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">User Registration</p>
                <p className="text-sm text-muted-foreground">Managed by backend configuration</p>
              </div>
              <input type="checkbox" aria-label="User registration enabled" defaultChecked className={checkboxClass} disabled />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Maintenance Mode</p>
                <p className="text-sm text-muted-foreground">Controlled via infrastructure</p>
              </div>
              <input type="checkbox" aria-label="Maintenance mode disabled" className={checkboxClass} disabled />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-muted-foreground">Depends on email provider integration</p>
              </div>
              <input type="checkbox" aria-label="Email notifications enabled" defaultChecked className={checkboxClass} disabled />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
